import React, { useState, useRef, useEffect } from 'react';
import { Outlet, useNavigate, useLocation, Link } from 'react-router-dom';
import { Box, Menu } from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { useAuth } from '../../contexts/AuthContext';
import EmailVerificationBanner from '../EmailVerificationBanner';
import { VellumLogo } from '../brand';
import { InputWithClear } from '@codex/components/inputs';
import PaidIcon from '@mui/icons-material/Paid';
import styled from '@emotion/styled';
import { fetchTokenBalance } from '../../services/api/tokenApi';
import { subscribeToTokenBalanceChanges } from '../../services/api/tokenBalanceEvents';
import { useNotifications } from '../../hooks/useNotifications';
import NotificationBell from './NotificationBell';
import StatusBadge from './StatusBadge';
import { NotificationsProvider } from '../../contexts/NotificationsContext';
import { useCookieConsent } from '../../contexts/CookieConsentContext';
import { useResponsiveNav } from '../../hooks/useResponsiveNav';
import { NewJournalMenuItems, type NewJournalKind } from '@/components/journals/NewJournalMenu';
import {
  AppShell,
  Nav,
  NavInner,
  NavLogo,
  NavLinks,
  NavLink,
  NavButton,
  NavMenuButton,
  NavUserArea,
  NavAvatar,
  AvatarDropdown,
  AvatarDropdownItem,
  AvatarDropdownDivider,
  NavSearchForm,
  MainContent,
  OutletWrapper,
  HamburgerButton,
  HamburgerBar,
  MobileOverlay,
  MobileDrawer,
  DrawerNavLink,
  DrawerNavButton,
  DrawerDivider,
  DrawerSignOutButton,
  AppFooter,
  AppFooterInner,
  AppFooterStatus,
  AppFooterNav,
  AppFooterLink,
  AppFooterExternalLink,
  AppFooterSep,
  AppFooterCookieButton,
} from './AppLayout.styled';

const DROPDOWN_ID = 'nav-user-menu';

const TokenChip = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 5px 11px;
  border-radius: 100px;
  border: 1px solid var(--color-border-default);
  background: var(--color-bg-elevated);
  color: var(--color-text-primary);
  font-size: 13px;
  font-weight: 600;
  text-decoration: none;
  transition:
    border-color 120ms ease,
    background 120ms ease;
  white-space: nowrap;
  svg {
    color: var(--color-primary);
    flex-shrink: 0;
  }
  &:hover {
    border-color: var(--color-primary);
    background: var(--color-bg-hover);
  }

  &:focus-visible {
    outline: 2px solid var(--color-border-focus);
    outline-offset: 2px;
  }
`;

export default function AppLayout() {
  const { user, isAuthenticated, logout } = useAuth();
  const { openPreferences } = useCookieConsent();
  const isStaff = user?.role === 'ADMIN' || user?.role === 'MODERATOR';
  const navigate = useNavigate();
  const location = useLocation();
  const { pathname, hash } = location;
  const isLandingPage = pathname === '/' && !isAuthenticated;
  // Pages that ship their own full marketing footer (PublicFooter). We skip
  // the AppLayout compact legal bar on these routes to avoid double footers.
  const isPublicPage = isLandingPage || pathname === '/pro' || pathname === '/referrals';
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [navSearchVal, setNavSearchVal] = useState('');
  const [tokenBalance, setTokenBalance] = useState<number | null>(null);
  // Anchor element for the desktop "Write" menu — non-null when open. Using
  // an anchor element rather than a separate boolean lets us pass the
  // anchor straight to <Menu anchorEl={...} open={Boolean(anchor)} />,
  // matching MUI's idiomatic Menu pattern.
  const [navWriteAnchor, setNavWriteAnchor] = useState<HTMLElement | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const mainRef = useRef<HTMLElement>(null);
  const prevPathname = useRef(pathname);

  // Refs for focus management (drawer open/close, dropdown toggle, etc.).
  const avatarBtnRef = useRef<HTMLButtonElement>(null);
  const hamburgerBtnRef = useRef<HTMLButtonElement>(null);
  const drawerRef = useRef<HTMLElement>(null);
  const firstDrawerLinkRef = useRef<HTMLAnchorElement | null>(null);

  // Content-aware nav switching: flips to a hamburger when the desktop nav
  // no longer fits, rather than relying on a fixed `max-width: 768px` media
  // query. See useResponsiveNav for the full rationale.
  //
  // Generic params match the actual elements the refs are attached to:
  //   NavInner  → MUI Box            → HTMLDivElement
  //   NavLogo   → react-router Link  → HTMLAnchorElement
  //   NavLinks  → styled('nav')      → HTMLElement
  const {
    containerRef: navContainerRef,
    logoRef: navLogoRef,
    navRef: navLinksRef,
    isCompact,
  } = useResponsiveNav<HTMLDivElement, HTMLAnchorElement, HTMLElement>();

  // Toggle the `inert` attribute on the hidden desktop nav so keyboard focus
  // skips it when we're in compact mode. We do this via an effect rather
  // than as a JSX prop because `inert` isn't in React 18's typed DOM props,
  // and all the workarounds (prop spread + cast, `as any`, etc.) are uglier
  // than just setting the attribute imperatively.
  useEffect(() => {
    const nav = navLinksRef.current;
    if (!nav) return;
    if (isCompact) {
      nav.setAttribute('inert', '');
    } else {
      nav.removeAttribute('inert');
    }
  }, [isCompact, navLinksRef]);

  // Mirror the same `inert` treatment on the mobile drawer when it's closed.
  // `aria-hidden` alone does NOT remove focusable elements from the tab
  // sequence — `inert` does. Without this, an off-screen drawer's links and
  // buttons remain reachable by Tab even though they're invisible.
  useEffect(() => {
    const drawer = drawerRef.current;
    if (!drawer) return;
    if (drawerOpen) {
      drawer.removeAttribute('inert');
    } else {
      drawer.setAttribute('inert', '');
    }
  }, [drawerOpen]);

  // If the viewport grows past the compact threshold while the drawer is
  // open (e.g. device rotation, window resize), close it — the drawer is
  // hidden and the user would otherwise be stuck with a phantom open state.
  useEffect(() => {
    if (!isCompact && drawerOpen) setDrawerOpen(false);
  }, [isCompact, drawerOpen]);

  const notificationState = useNotifications(isAuthenticated);

  // ── Close avatar dropdown on outside click ─────────────────────────────────
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // ── Close avatar dropdown on Escape, restore focus to the trigger ──────────
  useEffect(() => {
    if (!dropdownOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setDropdownOpen(false);
        avatarBtnRef.current?.focus();
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [dropdownOpen]);

  // ── Close mobile drawer on Escape, restore focus to the hamburger ─────────
  useEffect(() => {
    if (!drawerOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setDrawerOpen(false);
        hamburgerBtnRef.current?.focus();
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [drawerOpen]);

  // ── When the drawer opens, move focus into it so keyboard users land in
  //    the drawer rather than continuing to tab through the page behind it.
  useEffect(() => {
    if (!drawerOpen) return;
    // Wait for the inert attribute to be removed and the slide-in transform
    // to settle, otherwise focus may be silently dropped by the browser.
    const t = setTimeout(() => {
      firstDrawerLinkRef.current?.focus();
    }, 60);
    return () => clearTimeout(t);
  }, [drawerOpen]);

  // ── Close mobile drawer when viewport grows past breakpoint ───────────────
  useEffect(() => {
    const handler = () => {
      if (window.innerWidth > 768) setDrawerOpen(false);
    };
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  // ── Lock body scroll when drawer is open ──────────────────────────────────
  useEffect(() => {
    document.body.style.overflow = drawerOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [drawerOpen]);

  // ── Scroll to top on page navigation ──────────────────────────────────────
  useEffect(() => {
    if (!mainRef.current) return;
    if (pathname === prevPathname.current) return;
    prevPathname.current = pathname;
    if (!hash) mainRef.current.scrollTop = 0;
    // Close drawer on any navigation
    setDrawerOpen(false);
  }, [pathname, hash]);

  // ── Smooth scroll to hash anchors ─────────────────────────────────────────
  useEffect(() => {
    const main = mainRef.current;
    if (!main) return;
    const handleClick = (e: MouseEvent) => {
      const target = (e.target as Element).closest('a');
      if (!target) return;
      const href = target.getAttribute('href');
      if (!href || !href.startsWith('#') || href === '#') return;
      const id = href.slice(1);
      const el = document.getElementById(id);
      if (!el) return;
      e.preventDefault();
      const containerTop = main.getBoundingClientRect().top;
      const elTop = el.getBoundingClientRect().top;
      main.scrollTo({ top: main.scrollTop + (elTop - containerTop), behavior: 'smooth' });
    };
    main.addEventListener('click', handleClick);
    return () => main.removeEventListener('click', handleClick);
  }, []);

  // ── Smooth scroll to hash on load ─────────────────────────────────────────
  useEffect(() => {
    if (!hash || !mainRef.current) return;
    const id = hash.slice(1);
    const timer = setTimeout(() => {
      const el = document.getElementById(id);
      if (!el || !mainRef.current) return;
      const containerTop = mainRef.current.getBoundingClientRect().top;
      const elTop = el.getBoundingClientRect().top;
      mainRef.current.scrollTo({
        top: mainRef.current.scrollTop + (elTop - containerTop),
        behavior: 'smooth',
      });
    }, 80);
    return () => clearTimeout(timer);
  }, [pathname, hash]);

  // ── Fetch token balance ────────────────────────────────────────────────────
  useEffect(() => {
    if (!isAuthenticated) {
      setTokenBalance(null);
      return;
    }
    fetchTokenBalance()
      .then(setTokenBalance)
      .catch(() => null);
  }, [isAuthenticated, pathname]);

  // ── Live balance updates ──────────────────────────────────────────────────
  // Subscribe to in-app token-balance change events so the navbar pill
  // refreshes immediately when the viewer's balance changes — for example,
  // after an admin grant to themselves, or after sending a tip. The event
  // carries the new balance so we can update without a network round-trip
  // in the common case; if the publisher couldn't supply one we fall back
  // to a refetch.
  useEffect(() => {
    return subscribeToTokenBalanceChanges(user?.id ?? null, (detail) => {
      if (detail.newBalance != null) {
        setTokenBalance(detail.newBalance);
      } else {
        fetchTokenBalance()
          .then(setTokenBalance)
          .catch(() => null);
      }
    });
  }, [user?.id]);

  const handleLogout = async () => {
    setDropdownOpen(false);
    setDrawerOpen(false);
    await logout();
    navigate('/login');
  };

  const handleSearch = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (navSearchVal.trim()) navigate(`/search?q=${encodeURIComponent(navSearchVal.trim())}`);
  };

  const balanceLabel = tokenBalance === null ? '…' : tokenBalance.toLocaleString();

  return (
    <NotificationsProvider value={notificationState}>
      <AppShell>
        {/* Skip-to-content link — visible only on focus. First focusable
            element on every page so keyboard users can bypass the nav.
            Targets the <main> below via id="main-content" and tabIndex=-1. */}
        <a href="#main-content" className="sr-only sr-only-focusable">
          Skip to main content
        </a>

        {!isLandingPage && (
          <Nav>
            <NavInner ref={navContainerRef}>
              <NavLogo to="/" aria-label="Vellum home" ref={navLogoRef}>
                <VellumLogo iconHeight={42} />
                {/* Brand wordmark for screen readers — kept per design intent. */}
                <h1 className="sr-only">Vellum</h1>
              </NavLogo>

              {/* ── Desktop nav ─────────────────────────────────────────────
                  Always mounted so useResponsiveNav can measure its
                  intrinsic width. When isCompact, `$hidden` positions it
                  off-screen, `aria-hidden` hides from AT, and the `inert`
                  attribute (applied via an effect below since it isn't
                  typed in React 18) takes it out of the tab order. */}
              <NavLinks
                ref={navLinksRef}
                aria-label="Main navigation"
                $hidden={isCompact}
                aria-hidden={isCompact || undefined}
              >
                <NavSearchForm onSubmit={handleSearch} role="search">
                  <InputWithClear
                    value={navSearchVal}
                    onChange={(e) => setNavSearchVal(e.target.value)}
                    onClear={() => setNavSearchVal('')}
                    placeholder="Search…"
                    label=""
                    aria-label="Search journals"
                  />
                </NavSearchForm>

                {isAuthenticated ? (
                  <>
                    {/* "Write" used to be a one-shot link straight to the
                        standard journal editor. With CYOA stories, every
                        new-journal entry point in the app should let users
                        choose a kind first — see NewJournalSplitButton. The
                        nav uses a slimmer single-button-plus-menu variant
                        (rather than the split button) because a chevron
                        next to "Write" in the top nav adds visual noise
                        every other route doesn't have. */}
                    <NavMenuButton
                      type="button"
                      onClick={(e) =>
                        setNavWriteAnchor((curr) =>
                          curr ? null : (e.currentTarget as HTMLElement),
                        )
                      }
                      aria-haspopup="menu"
                      aria-expanded={navWriteAnchor ? 'true' : undefined}
                      aria-label="Create a new journal"
                    >
                      Write
                      <ArrowDropDownIcon sx={{ fontSize: 18, ml: -0.25 }} />
                    </NavMenuButton>
                    <Menu
                      anchorEl={navWriteAnchor}
                      open={Boolean(navWriteAnchor)}
                      onClose={() => setNavWriteAnchor(null)}
                      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                      // disableAutoFocusItem mirrors how the avatar dropdown
                      // behaves below — opening with a click shouldn't yank
                      // focus from where the user was reading.
                      disableAutoFocusItem
                      MenuListProps={{ dense: true, sx: { minWidth: 240 } }}
                    >
                      <NewJournalMenuItems
                        onPick={(kind: NewJournalKind) => {
                          setNavWriteAnchor(null);
                          if (kind === 'INTERACTIVE') {
                            navigate('/write', { state: { kind: 'INTERACTIVE' } });
                          } else {
                            navigate('/write');
                          }
                        }}
                      />
                    </Menu>
                    <NavLink to="/my-journals">My Journals</NavLink>
                    <NavLink to="/bookmarks">Bookmarks</NavLink>
                    {user?.isCreator && <NavLink to="/dashboard">Dashboard</NavLink>}

                    <TokenChip to="/tokens" aria-label={`Token balance: ${balanceLabel}`}>
                      <PaidIcon sx={{ fontSize: 15 }} aria-hidden="true" />
                      <span>{balanceLabel}</span>
                    </TokenChip>

                    <NotificationBell notifications={notificationState} />

                    <NavUserArea ref={dropdownRef}>
                      {/* NavAvatar is now a real <button> — the previous
                          <Link> with preventDefault announced as a link to
                          a profile while functioning as a menu toggle. */}
                      <NavAvatar
                        ref={avatarBtnRef}
                        type="button"
                        onClick={() => setDropdownOpen((o) => !o)}
                        aria-expanded={dropdownOpen}
                        aria-haspopup="menu"
                        aria-controls={DROPDOWN_ID}
                        aria-label={`${user?.displayName ?? user?.username ?? 'User'} menu`}
                      >
                        {user?.avatar ? (
                          // Decorative — the button's aria-label already names it.
                          <img src={user.avatar} alt="" />
                        ) : (
                          <span aria-hidden="true">
                            {(user?.displayName ?? user?.username ?? '?')[0].toUpperCase()}
                          </span>
                        )}
                      </NavAvatar>

                      {dropdownOpen && (
                        <AvatarDropdown id={DROPDOWN_ID}>
                          <AvatarDropdownItem
                            to={`/u/${user?.username}`}
                            onClick={() => setDropdownOpen(false)}
                          >
                            View profile
                          </AvatarDropdownItem>
                          <AvatarDropdownItem
                            to="/collaborations"
                            onClick={() => setDropdownOpen(false)}
                          >
                            My collaborations
                          </AvatarDropdownItem>
                          <AvatarDropdownItem
                            to="/referrals"
                            onClick={() => setDropdownOpen(false)}
                          >
                            Referral program
                          </AvatarDropdownItem>
                          <AvatarDropdownItem to="/tokens" onClick={() => setDropdownOpen(false)}>
                            <PaidIcon
                              sx={{ fontSize: 15, verticalAlign: 'middle', mr: 0.75, mb: '1px' }}
                              aria-hidden="true"
                            />
                            <span>Tokens ({balanceLabel})</span>
                          </AvatarDropdownItem>
                          <AvatarDropdownItem to="/settings" onClick={() => setDropdownOpen(false)}>
                            Settings
                          </AvatarDropdownItem>
                          <AvatarDropdownItem
                            to="/my-reports"
                            onClick={() => setDropdownOpen(false)}
                          >
                            Content reports
                          </AvatarDropdownItem>
                          <AvatarDropdownDivider />
                          <AvatarDropdownItem
                            {...({
                              as: 'button',
                              to: '/',
                              onClick: handleLogout,
                            } as unknown as React.ComponentPropsWithoutRef<
                              typeof AvatarDropdownItem
                            >)}
                          >
                            Sign out
                          </AvatarDropdownItem>
                        </AvatarDropdown>
                      )}
                    </NavUserArea>
                  </>
                ) : (
                  <>
                    <NavLink to="/login">Sign in</NavLink>
                    <NavButton to="/register">Get started</NavButton>
                  </>
                )}
                {isStaff && <NavLink to="/admin">Admin</NavLink>}
              </NavLinks>

              {/* ── Compact (hamburger) area ────────────────────────────
                  Rendered only when useResponsiveNav says the desktop nav
                  wouldn't fit. No CSS media query — the decision is made
                  based on measured content width. */}
              {isCompact && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--spacing-2)',
                    flexShrink: 0,
                  }}
                >
                  {isAuthenticated && <NotificationBell notifications={notificationState} />}
                  <HamburgerButton
                    ref={hamburgerBtnRef}
                    type="button"
                    aria-label={drawerOpen ? 'Close menu' : 'Open menu'}
                    aria-expanded={drawerOpen}
                    aria-controls="mobile-drawer"
                    onClick={() => setDrawerOpen((o) => !o)}
                  >
                    <HamburgerBar $open={drawerOpen} $i={0} />
                    <HamburgerBar $open={drawerOpen} $i={1} />
                    <HamburgerBar $open={drawerOpen} $i={2} />
                  </HamburgerButton>
                </Box>
              )}
            </NavInner>
          </Nav>
        )}

        {!isLandingPage && <EmailVerificationBanner />}

        {/* ── Compact-mode overlay + drawer ────────────────────────────────
            Rendered only when the nav is in compact mode. Closed-state
            tab-stops are removed via the `inert` attribute applied in an
            effect (see useEffect on drawerOpen above) — `aria-hidden`
            alone does not remove focus from the tab order. */}
        {isCompact && (
          <>
            <MobileOverlay $open={drawerOpen} onClick={() => setDrawerOpen(false)} aria-hidden />

            <MobileDrawer
              id="mobile-drawer"
              ref={drawerRef}
              $open={drawerOpen}
              aria-label="Mobile navigation"
            >
              {/* Search */}
              <NavSearchForm
                onSubmit={(e) => {
                  handleSearch(e);
                  setDrawerOpen(false);
                }}
                role="search"
                style={{ width: '100%', marginBottom: 'var(--spacing-4)' }}
              >
                <InputWithClear
                  value={navSearchVal}
                  onChange={(e) => setNavSearchVal(e.target.value)}
                  onClear={() => setNavSearchVal('')}
                  placeholder="Search…"
                  label=""
                  aria-label="Search journals"
                />
              </NavSearchForm>

              {isAuthenticated ? (
                <>
                  {/* Two stacked entry points so mobile users get the same
                      kind-choice the desktop nav menu offers. We keep the
                      first-drawer-link ref on the standard option (the more
                      common path) so focus management on drawer-open is
                      unchanged. The interactive option uses react-router's
                      `state` prop, which the editor reads via useLocation
                      to seed an INTERACTIVE journal — same wire format the
                      NewJournalSplitButton uses elsewhere. */}
                  <DrawerNavButton
                    to="/write"
                    onClick={() => setDrawerOpen(false)}
                    ref={firstDrawerLinkRef as React.Ref<HTMLAnchorElement>}
                  >
                    New journal
                  </DrawerNavButton>
                  <DrawerNavButton
                    to="/write"
                    state={{ kind: 'INTERACTIVE' }}
                    onClick={() => setDrawerOpen(false)}
                  >
                    New interactive story
                  </DrawerNavButton>
                  <DrawerDivider />
                  <DrawerNavLink to="/my-journals" onClick={() => setDrawerOpen(false)}>
                    My Journals
                  </DrawerNavLink>
                  <DrawerNavLink to="/bookmarks" onClick={() => setDrawerOpen(false)}>
                    Bookmarks
                  </DrawerNavLink>
                  <DrawerNavLink to="/notifications" onClick={() => setDrawerOpen(false)}>
                    Notifications
                    {notificationState.unreadCount > 0 ? ` (${notificationState.unreadCount})` : ''}
                  </DrawerNavLink>
                  {user?.isCreator && (
                    <DrawerNavLink to="/dashboard" onClick={() => setDrawerOpen(false)}>
                      Dashboard
                    </DrawerNavLink>
                  )}
                  <DrawerNavLink to="/tokens" onClick={() => setDrawerOpen(false)}>
                    Tokens ({balanceLabel})
                  </DrawerNavLink>
                  <DrawerDivider />
                  <DrawerNavLink to={`/u/${user?.username}`} onClick={() => setDrawerOpen(false)}>
                    View profile
                  </DrawerNavLink>
                  <DrawerNavLink to="/collaborations" onClick={() => setDrawerOpen(false)}>
                    My collaborations
                  </DrawerNavLink>
                  <DrawerNavLink to="/referrals" onClick={() => setDrawerOpen(false)}>
                    Referral program
                  </DrawerNavLink>
                  <DrawerNavLink to="/settings" onClick={() => setDrawerOpen(false)}>
                    Settings
                  </DrawerNavLink>
                  <DrawerNavLink to="/my-reports" onClick={() => setDrawerOpen(false)}>
                    Content reports
                  </DrawerNavLink>
                  {isStaff && (
                    <DrawerNavLink to="/admin" onClick={() => setDrawerOpen(false)}>
                      Admin
                    </DrawerNavLink>
                  )}
                  <DrawerDivider />
                  <DrawerSignOutButton type="button" onClick={handleLogout}>
                    Sign out
                  </DrawerSignOutButton>
                </>
              ) : (
                <>
                  <DrawerNavLink
                    to="/login"
                    onClick={() => setDrawerOpen(false)}
                    ref={firstDrawerLinkRef as React.Ref<HTMLAnchorElement>}
                  >
                    Sign in
                  </DrawerNavLink>
                  <DrawerNavButton to="/register" onClick={() => setDrawerOpen(false)}>
                    Get started
                  </DrawerNavButton>
                </>
              )}
            </MobileDrawer>
          </>
        )}

        <MainContent ref={mainRef} id="main-content" tabIndex={-1}>
          <OutletWrapper>
            <Outlet />
          </OutletWrapper>
          {/* Compact legal footer — the public pages (LandingPage, ProPage,
              ReferralPage) ship their own multi-column marketing footer via
              <PublicFooter>, so we skip this one there to avoid doubling up.
              The OutletWrapper above grows to fill leftover height, which
              pins this footer to the bottom of the viewport when the page
              content is shorter than the scroll container (classic flexbox
              sticky-footer pattern). */}
          {!isPublicPage && (
            <AppFooter>
              <AppFooterInner>
                <AppFooterNav>
                  <span>© {new Date().getFullYear()} Vellum</span>
                </AppFooterNav>

                <AppFooterNav aria-label="Legal links">
                  <AppFooterLink to="/legal/terms">Terms</AppFooterLink>
                  <AppFooterSep aria-hidden="true">·</AppFooterSep>
                  <AppFooterLink to="/legal/privacy">Privacy</AppFooterLink>
                  <AppFooterSep aria-hidden="true">·</AppFooterSep>
                  <AppFooterLink to="/legal/cookies">Cookies</AppFooterLink>
                  <AppFooterSep aria-hidden="true">·</AppFooterSep>
                  <AppFooterLink to="/legal/dmca">DMCA</AppFooterLink>
                  <AppFooterSep aria-hidden="true">·</AppFooterSep>
                  <AppFooterLink to="/legal/refunds">Refunds</AppFooterLink>
                  <AppFooterSep aria-hidden="true">·</AppFooterSep>
                  <AppFooterLink to="/legal/community-guidelines">
                    Community Guidelines
                  </AppFooterLink>
                  <AppFooterSep aria-hidden="true">·</AppFooterSep>
                  <AppFooterLink to="/legal/csam-policy">CSAM Policy</AppFooterLink>
                  <AppFooterSep aria-hidden="true">·</AppFooterSep>
                  <AppFooterExternalLink
                    href="https://status.writevellum.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Status
                  </AppFooterExternalLink>
                  <AppFooterSep aria-hidden="true">·</AppFooterSep>
                  <AppFooterCookieButton type="button" onClick={openPreferences}>
                    Cookie preferences
                  </AppFooterCookieButton>
                </AppFooterNav>

                {/* Live service status badge. Self-rendered (see
                    StatusBadge.tsx) — the previous BetterStack iframe was
                    cross-origin which blocked any styling/centering from
                    the parent. The new component polls /api/status on the
                    backend, which proxies + caches BetterStack's status
                    JSON, and animates a coloured dot driven off the
                    response. */}
                <AppFooterStatus>
                  <StatusBadge />
                </AppFooterStatus>
              </AppFooterInner>
            </AppFooter>
          )}
        </MainContent>
      </AppShell>
    </NotificationsProvider>
  );
}
